import { useEffect, useState } from "react";

export type Theme =
  | "light"
  | "dark"
  | "synthwave"
  | "valentine"
  | "halloween"
  | "forest"
  | "aqua"
  | "lofi"
  | "luxury"
  | "dracula"
  | "autumn"
  | "business"
  | "lemonade"
  | "night"
  | "coffee"
  | "winter"
  | "retro"
  | "minimal"
  | "print"
  | "blueprint"
  | "system";

interface ThemeState {
  theme: Theme;
  systemTheme: "light" | "dark";
}

export const useTheme = () => {
  const [state, setState] = useState<ThemeState>(() => {
    const savedTheme = localStorage.getItem("theme") as Theme | null;
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
      .matches
      ? "dark"
      : "light";
    return {
      theme: savedTheme || "system",
      systemTheme,
    };
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      setState((prev) => ({
        ...prev,
        systemTheme: e.matches ? "dark" : "light",
      }));
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const effectiveTheme =
      state.theme === "system" ? state.systemTheme : state.theme;
    document.documentElement.setAttribute("data-theme", effectiveTheme);
  }, [state.theme, state.systemTheme]);

  const setTheme = (newTheme: Theme) => {
    setState((prev) => ({ ...prev, theme: newTheme }));
    localStorage.setItem("theme", newTheme);
  };

  return {
    theme: state.theme,
    effectiveTheme: state.theme === "system" ? state.systemTheme : state.theme,
    setTheme,
  };
};
